import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Card,
  CardContent,
  IconButton,
  MenuItem,
  Paper,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  checkSubmittedInvoicesApi,
  getBuyerSuggestionsApi,
  getFbrInvoiceTypes,
  getFbrProvinces,
  getFbrRates,
  getFbrSroItems,
  getFbrSroSchedules,
  getFbrTransactionTypes,
  getFbrUoms,
} from "../api";
import { useData } from "../ContextApi/ContextProvider";

// Helper function: chunk array for rows
function chunkArray(arr, chunkSize) {
  const result = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    result.push(arr.slice(i, i + chunkSize));
  }
  return result;
}

const pick = (obj, keys) => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null) return obj[key];
  }
  return "";
};

const normalizeLookup = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const getProvinceDesc = (value, provinces, fallback = "") => {
  const normalizedValue = normalizeLookup(value);
  const matched = provinces.find((province) => {
    const desc = normalizeLookup(province.stateProvinceDesc);
    const code = normalizeLookup(province.stateProvinceCode);
    return desc === normalizedValue || code === normalizedValue;
  });

  return matched?.stateProvinceDesc || fallback || provinces[0]?.stateProvinceDesc || "";
};

const getSaleTypeId = (opt) => pick(opt, ["transactioN_TYPE_ID", "transactionTypeId", "transTypeId", "id"]);
const getSaleTypeDesc = (opt) => pick(opt, ["transactioN_DESC", "transactionDesc", "description", "desc"]);
const getRateId = (opt) => pick(opt, ["ratE_ID", "rateId", "rate_id", "id"]);
const getRateDesc = (opt) => pick(opt, ["ratE_DESC", "rateDesc", "rate", "description", "desc"]);
const getSroId = (opt) => pick(opt, ["srO_ID", "sroId", "sro_id", "id"]);
const getSroDesc = (opt) => pick(opt, ["srO_DESC", "sroDesc", "sroScheduleNo", "description", "desc"]);
const getSroItemId = (opt) => pick(opt, ["srO_ITEM_ID", "sroItemId", "sro_item_id", "id"]);
const getSroItemDesc = (opt) => pick(opt, ["srO_ITEM_DESC", "sroItemDesc", "sroItemSerialNo", "description", "desc"]);
const getHsCode = (opt) => pick(opt, ["hS_CODE", "hsCode", "hs_code", "code"]);
const getHsDesc = (opt) => pick(opt, ["description", "desc", "hS_DESC", "hsDesc"]);
const getHsUom = (opt) => pick(opt, ["configuredUom", "uom", "uoM"]);
const getHsSaleType = (opt) => pick(opt, ["configuredSaleType", "sale_type", "saleType"]);
const getHsSaleTypeId = (opt) => pick(opt, ["configuredSaleTypeId", "sale_type_id", "saleTypeId"]);
const getHsRate = (opt) => pick(opt, ["configuredRate", "rate"]);
const getHsRateId = (opt) => pick(opt, ["configuredRateId", "rate_id", "rateId"]);
const getHsSroScheduleNo = (opt) => pick(opt, ["configuredSroScheduleNo", "sro_schedule_no", "sroScheduleNo"]);
const getHsSroScheduleId = (opt) => pick(opt, ["configuredSroScheduleId", "sro_schedule_id", "sroScheduleId"]);
const getHsSroItemNo = (opt) => pick(opt, ["configuredSroItemNo", "sro_item_no", "sroItemNo", "sroItemSerialNo"]);
const getScenarioId = (opt) => pick(opt, ["scenario_id", "scenarioId", "id"]);
const DEFAULT_SALE_TYPE = "Goods at standard rate (default)";
const isDefaultSaleType = (value) =>
  !value || String(value).trim().toLowerCase() === DEFAULT_SALE_TYPE.toLowerCase();

const initialItem = {
  hsCode: "",
  productDescription: "",
  rate: "",
  rateId: "",
  uoM: "",
  quantity: 1,
  totalValues: 0,
  valueSalesExcludingST: 0,
  fixedNotifiedValueOrRetailPrice: 0,
  salesTaxApplicable: 0,
  salesTaxWithheldAtSource: 0,
  extraTax: "",
  furtherTax: 0,
  sroScheduleNo: "",
  fedPayable: 0,
  discount: 0,
  saleType: "",
  saleTypeId: "",
  sroItemSerialNo: "",
  sroScheduleId: "",
};

const itemFields = [
  { key: "hsCode", label: "HS Code" },
  { key: "productDescription", label: "Description" },
  { key: "saleType", label: "Sale Type" },
  { key: "rate", label: "Rate (%)" },
  { key: "sroScheduleNo", label: "SRO Schedule" },
  { key: "sroItemSerialNo", label: "SRO Item #" },
  { key: "uoM", label: "Unit" },
  { key: "quantity", label: "Qty" },
  { key: "totalValues", label: "Total Value" },
  { key: "valueSalesExcludingST", label: "Value excl ST" },
  { key: "fixedNotifiedValueOrRetailPrice", label: "Fixed Value" },
  { key: "salesTaxApplicable", label: "Sales Tax" },
  { key: "salesTaxWithheldAtSource", label: "ST Withheld" },
  { key: "extraTax", label: "Extra Tax" },
  { key: "furtherTax", label: "Further Tax" },
  { key: "fedPayable", label: "FED Payable" },
  { key: "discount", label: "Discount" },
];



const itemSchema = Yup.object().shape({
  hsCode: Yup.string().required("HS Code required"),
  productDescription: Yup.string().required("Description required"),
  rate: Yup.string().required("Rate required"),
  uoM: Yup.string().required("Unit required"),
  quantity: Yup.number().positive().required("Quantity required"),
  totalValues: Yup.number(),
  valueSalesExcludingST: Yup.number().required("Value excl ST"),
  fixedNotifiedValueOrRetailPrice: Yup.number(),
  salesTaxApplicable: Yup.number().required("ST applicable"),
  salesTaxWithheldAtSource: Yup.number(),
  extraTax: Yup.string(),
  furtherTax: Yup.number().required("Further tax"),
  sroScheduleNo: Yup.string().test(
    "sro-schedule-required",
    "SRO schedule required for this sale type",
    function (value) {
      return isDefaultSaleType(this.parent.saleType) || Boolean(String(value || "").trim());
    }
  ),
  fedPayable: Yup.number(),
  discount: Yup.number(),
  saleType: Yup.string().required("Sale type required"),
  sroItemSerialNo: Yup.string().test(
    "sro-item-required",
    "SRO item required for this sale type",
    function (value) {
      return isDefaultSaleType(this.parent.saleType) || Boolean(String(value || "").trim());
    }
  ),
});

const hasPendingItemData = (item) =>
  [
    "hsCode",
    "productDescription",
    "rate",
    "totalValues",
    "valueSalesExcludingST",
    "fixedNotifiedValueOrRetailPrice",
    "salesTaxApplicable",
    "salesTaxWithheldAtSource",
    "extraTax",
    "furtherTax",
    "sroScheduleNo",
    "fedPayable",
    "discount",
    "sroItemSerialNo",
  ].some((key) => {
    const value = item?.[key];
    return value !== undefined && value !== null && String(value).trim() !== "" && Number(value) !== 0;
  });

const validationSchema = Yup.object().shape({
  invoiceType: Yup.string().required(),
  invoiceDate: Yup.string().required(),
  sellerNTNCNIC: Yup.string().required(),
  sellerBusinessName: Yup.string().required(),
  sellerProvince: Yup.string().required(),
  sellerAddress: Yup.string().required(),
  buyerNTNCNIC: Yup.string().required(),
  buyerBusinessName: Yup.string().required(),
  buyerProvince: Yup.string().required(),
  buyerAddress: Yup.string().required(),
  buyerRegistrationType: Yup.string().required(),
  // items: Yup.array().min(1, "At least one item required"),
});

const InvoiceForm = ({ open, onClose, onSave }) => {
  const { globalData } = useData();
  // Dropdown data
  const [provinces, setProvinces] = useState([]);
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [saleTypes, setSaleTypes] = useState([]);
  const [rates, setRates] = useState([]);
  const [sroSchedules, setSroSchedules] = useState([]);
  const [sroItems, setSroItems] = useState([]);

  const [uoms, setUoms] = useState([]);
  const [formInitDone, setFormInitDone] = useState(false);
const [registrationTypes]=useState([
  { statuscode: "00", REGISTRATION_TYPE: "Registered" },
  { statuscode: "01", REGISTRATION_TYPE: "Unregistered" },
])
  // Item form state
  const [itemForm, setItemForm] = useState(initialItem);
  const [itemEditIndex, setItemEditIndex] = useState(null);
  const [itemError, setItemError] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [invoiceNumberCheck, setInvoiceNumberCheck] = useState({
    checking: false,
    exists: false,
    message: "",
  });
  const [buyerSuggestions, setBuyerSuggestions] = useState([]);
  const [showBuyerSuggestions, setShowBuyerSuggestions] = useState(false);
  const scenarioOptions = Array.isArray(globalData?.business_scenarios)
    ? globalData.business_scenarios.map(getScenarioId).filter(Boolean)
    : [];

  useEffect(() => {
    if (!open || !globalData) return;

    Promise.all([
      getFbrProvinces(globalData),
      getFbrInvoiceTypes(globalData),
      getFbrUoms(globalData),
      getFbrTransactionTypes(globalData),
    ])
      .then(([provinceData, invoiceTypeData, uomData, saleTypeData]) => {
        setProvinces(provinceData || []);
        setInvoiceTypes(invoiceTypeData || []);
        setUoms(uomData || []);
        setSaleTypes(saleTypeData || []);
      })
      .catch((error) => {
        console.error("Failed to fetch FBR reference data:", error);
      });
  }, [open, globalData]);

  const [setFieldValueRef, setSetFieldValueRef] = useState(null);

  useEffect(() => {
    if (
      setFieldValueRef &&
      !formInitDone &&
      provinces.length &&
      invoiceTypes.length &&
      registrationTypes.length
    ) {
      setFieldValueRef("sellerNTNCNIC", globalData?.ntn_cnic || "");
      setFieldValueRef("sellerBusinessName", globalData?.company_name || globalData?.full_name || "");
      setFieldValueRef("sellerProvince", getProvinceDesc(globalData?.province, provinces));
      setFieldValueRef("sellerAddress", globalData?.business_address || "");
      setFieldValueRef("buyerProvince", provinces[0].stateProvinceDesc);
      setFieldValueRef("invoiceType", invoiceTypes[0].docDescription);
      setFieldValueRef("buyerRegistrationType", registrationTypes[0].REGISTRATION_TYPE);
      setFieldValueRef("scenarioId", scenarioOptions[0] || globalData?.scenarioId || "");
      setFormInitDone(true);
    }
    setItemForm((itemForm) => ({
      ...itemForm,
      uoM: uoms.length ? uoms[0].description : "",
      saleType:saleTypes.length ? getSaleTypeDesc(saleTypes[0]) : "",
      saleTypeId:saleTypes.length ? getSaleTypeId(saleTypes[0]) : "",
    }));
    // eslint-disable-next-line
  }, [
    provinces,
    invoiceTypes,
    registrationTypes,
    saleTypes,
    uoms,
    globalData,
    setFieldValueRef,
  ]);

  // Validate a single item using Yup
  const validateItem = async (item) => {
    try {
      await itemSchema.validate(item, { abortEarly: false });
      return {};
    } catch (err) {
      const errors = {};
      err.inner.forEach((e) => (errors[e.path] = e.message));
      return errors;
    }
  };

  const checkInvoiceNumber = async (invoiceNumber) => {
    const value = String(invoiceNumber || "").trim();
    if (!value) {
      setInvoiceNumberCheck({ checking: false, exists: false, message: "" });
      return false;
    }

    setInvoiceNumberCheck({ checking: true, exists: false, message: "Checking invoice number..." });

    try {
      const submittedInvoiceNumbers = await checkSubmittedInvoicesApi(
        [value],
        globalData?.is_prod_token
      );
      const exists = submittedInvoiceNumbers
        .map((submittedInvoiceNumber) => String(submittedInvoiceNumber || "").trim())
        .includes(value);

      setInvoiceNumberCheck({
        checking: false,
        exists,
        message: exists ? "This invoice is already submitted." : "",
      });

      return exists;
    } catch (error) {
      console.error("Failed to check invoice number:", error);
      setInvoiceNumberCheck({
        checking: false,
        exists: false,
        message: "Could not verify this invoice number. It will be checked again on save.",
      });
      return false;
    }
  };

  const fetchBuyerSuggestions = async (value) => {
    const term = String(value || "").trim();
    if (term.length < 2) {
      setBuyerSuggestions([]);
      setShowBuyerSuggestions(false);
      return;
    }

    try {
      const suggestions = await getBuyerSuggestionsApi(term);
      setBuyerSuggestions(suggestions);
      setShowBuyerSuggestions(suggestions.length > 0);
    } catch (error) {
      console.error("Failed to fetch buyer suggestions:", error);
      setBuyerSuggestions([]);
      setShowBuyerSuggestions(false);
    }
  };

  const applyBuyerSuggestion = (buyer, values, setFieldValue) => {
    const buyerNtn = pick(buyer, ["buyer_ntn_cnic", "buyerNTNCNIC", "buyerNtnCnic"]);
    const buyerSalesTaxNo = pick(buyer, ["buyer_sales_tax_no", "buyerSalesTaxNo", "buyerSalesTaxNumber"]);
    const buyerBusinessName = pick(buyer, ["buyer_business_name", "buyerBusinessName"]);
    const buyerProvince = pick(buyer, ["buyer_province", "buyerProvince"]);
    const buyerAddress = pick(buyer, ["buyer_address", "buyerAddress"]);
    const buyerRegistrationType = pick(buyer, ["buyer_registration_type", "buyerRegistrationType"]);
    const termsOfSale = pick(buyer, ["terms_of_sale", "termsOfSale"]);

    setFieldValue("buyerNTNCNIC", buyerNtn || "");
    setFieldValue("buyerSalesTaxNo", buyerSalesTaxNo || "");
    setFieldValue("buyerBusinessName", buyerBusinessName || "");
    setFieldValue("buyerProvince", getProvinceDesc(buyerProvince, provinces, values.buyerProvince));
    setFieldValue("buyerAddress", buyerAddress || "");
    setFieldValue("buyerRegistrationType", buyerRegistrationType || values.buyerRegistrationType);
    setFieldValue("termsOfSale", termsOfSale || values.termsOfSale);
    setShowBuyerSuggestions(false);
  };

  const renderBuyerSuggestions = (values, setFieldValue) => showBuyerSuggestions && buyerSuggestions.length > 0 ? (
    <Paper
      variant="outlined"
      sx={{ mt: 1, maxHeight: 180, overflow: "auto", borderRadius: 2 }}
    >
      {buyerSuggestions.map((buyer, index) => (
        <Box
          key={`${pick(buyer, ["buyer_ntn_cnic", "buyerNTNCNIC"]) || pick(buyer, ["buyer_sales_tax_no", "buyerSalesTaxNo"]) || "buyer"}-${index}`}
          onMouseDown={(event) => {
            event.preventDefault();
            applyBuyerSuggestion(buyer, values, setFieldValue);
          }}
          sx={{
            px: 1.5,
            py: 1,
            cursor: "pointer",
            borderBottom: index === buyerSuggestions.length - 1 ? "none" : "1px solid #e5e7eb",
            "&:hover": { bgcolor: "#f8fafc" },
          }}
        >
          <Typography fontWeight={700} fontSize={14}>
            {pick(buyer, ["buyer_business_name", "buyerBusinessName"]) || "Unnamed Buyer"}
          </Typography>
          <Typography color="text.secondary" fontSize={12}>
            {pick(buyer, ["buyer_ntn_cnic", "buyerNTNCNIC"]) || "-"} {pick(buyer, ["buyer_sales_tax_no", "buyerSalesTaxNo"]) ? `- ${pick(buyer, ["buyer_sales_tax_no", "buyerSalesTaxNo"])}` : ""} {pick(buyer, ["buyer_province", "buyerProvince"]) ? `- ${pick(buyer, ["buyer_province", "buyerProvince"])}` : ""}
          </Typography>
        </Box>
      ))}
    </Paper>
  ) : null;

  const fetchRatesForItem = async (values, item = itemForm) => {
    const province = provinces.find((opt) => opt.stateProvinceDesc === values.sellerProvince);
    if (!values.invoiceDate || !item.saleTypeId || !province?.stateProvinceCode) return [];

    const rateData = await getFbrRates(
      {
        date: values.invoiceDate,
        transTypeId: item.saleTypeId,
        originationSupplier: province.stateProvinceCode,
      },
      globalData
    );
    setRates(rateData || []);
    return rateData || [];
  };

  const fetchSroSchedulesForItem = async (values, item = itemForm) => {
    const province = provinces.find((opt) => opt.stateProvinceDesc === values.sellerProvince);
    if (!values.invoiceDate || !item.rateId || !province?.stateProvinceCode) return [];

    const sroData = await getFbrSroSchedules(
      {
        rateId: item.rateId,
        date: values.invoiceDate,
        originationSupplier: province.stateProvinceCode,
      },
      globalData
    );
    setSroSchedules(sroData || []);
    return sroData || [];
  };

  const fetchSroItemsForItem = async (values, item = itemForm) => {
    if (!values.invoiceDate || !item.sroScheduleId) return [];

    const sroItemData = await getFbrSroItems(
      {
        date: values.invoiceDate,
        sroId: item.sroScheduleId,
      },
      globalData
    );
    setSroItems(sroItemData || []);
    return sroItemData || [];
  };

  const resetRateDependencies = () => {
    setItemForm((prev) => ({
      ...prev,
      rate: "",
      rateId: "",
      sroScheduleNo: "",
      sroScheduleId: "",
      sroItemSerialNo: "",
    }));
    setRates([]);
    setSroSchedules([]);
    setSroItems([]);
  };

  const canLoadRates = (values, item = itemForm) =>
    Boolean(values.invoiceDate && values.sellerProvince && item.saleTypeId);

  const canLoadSroSchedules = (values, item = itemForm) =>
    Boolean(values.invoiceDate && values.sellerProvince && item.rateId);

  const canLoadSroItems = (values, item = itemForm) =>
    Boolean(values.invoiceDate && item.sroScheduleId);

  const isItemSelectDisabled = (field, values) => {
    if (field === "rate") return !canLoadRates(values);
    if (field === "sroScheduleNo") return !canLoadSroSchedules(values);
    if (field === "sroItemSerialNo") return !canLoadSroItems(values);
    return false;
  };

  const getItemSelectHelperText = (field, values) => {
    if (field === "rate" && !canLoadRates(values)) {
      return "Select invoice date, seller province, and sale type first.";
    }
    if (field === "sroScheduleNo" && !canLoadSroSchedules(values)) {
      return "Select rate first.";
    }
    if (field === "sroItemSerialNo" && !canLoadSroItems(values)) {
      return "Select SRO schedule first.";
    }
    return "";
  };

  // Split fields for 3 in a row
  const itemFieldsRows = chunkArray(itemFields, 3);
  const configuredHsCodes = Array.isArray(globalData?.hs_codes) ? globalData.hs_codes : [];
  const availableHsCodes = configuredHsCodes.length
    ? configuredHsCodes.map((item) => ({
        hS_CODE: item.hs_code,
        description: item.description,
        configuredUom: item.uom,
        configuredSaleType: item.sale_type || item.saleType,
        configuredSaleTypeId: item.sale_type_id || item.saleTypeId,
        configuredRate: item.rate,
        configuredRateId: item.rate_id || item.rateId,
        configuredSroScheduleNo: item.sro_schedule_no || item.sroScheduleNo,
        configuredSroScheduleId: item.sro_schedule_id || item.sroScheduleId,
        configuredSroItemNo: item.sro_item_no || item.sroItemNo || item.sroItemSerialNo,
      }))
    : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Formik
        initialValues={{
          invoiceType: "",
          invoiceDate: "",
          invoiceNumber: "",
          sellerNTNCNIC: "",
          sellerBusinessName: "",
          sellerProvince: "",
          sellerAddress: "",
          buyerNTNCNIC: "",
          buyerSalesTaxNo: "",
          buyerBusinessName: "",
          buyerProvince: "",
          buyerAddress: "",
          buyerRegistrationType: "",
          termsOfSale: "",
          invoiceRefNo: "",
          scenarioId: "",
          reason: "",
          items: [],
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, actions) => {
          setSubmitting(true);
          const invoiceAlreadySubmitted = await checkInvoiceNumber(values.invoiceNumber);
          if (invoiceAlreadySubmitted) {
            setSubmitting(false);
            actions.setSubmitting(false);
            return;
          }

          let finalValues = {
            ...values,
            items: [...(values.items || [])],
          };

          if (hasPendingItemData(itemForm)) {
            const errors = await validateItem(itemForm);
            setItemError(errors);
            if (Object.keys(errors).length > 0) {
              setSubmitting(false);
              actions.setSubmitting(false);
              return;
            }

            if (itemEditIndex !== null) {
              finalValues.items[itemEditIndex] = itemForm;
            } else {
              finalValues.items.push(itemForm);
            }
          }

          if (!finalValues.items.length) {
            setItemError({ productDescription: "Add at least one item before saving." });
            setSubmitting(false);
            actions.setSubmitting(false);
            return;
          }

          await onSave(finalValues);
          setSubmitting(false);
          actions.resetForm();
          setItemForm(initialItem);
          setFormInitDone(false);
          onClose();
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          handleSubmit,
        }) => {
          useEffect(() => {
            setSetFieldValueRef(() => setFieldValue);
          }, [setFieldValue]);
          return (
            <form onSubmit={handleSubmit}>
              <DialogTitle
                fontWeight={700}
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 2 }}
              >
                Invoice Detail
                <Tooltip title="Close">
                  <span>
                    <IconButton
                      size="small"
                      onClick={onClose}
                      disabled={submitting}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </DialogTitle>
              <DialogContent>
                <Typography variant="h6" fontWeight={600} mb={2}>
                  Summary
                </Typography>
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <TextField
                          select
                          name="invoiceType"
                          label="Invoice Type"
                          fullWidth
                          value={values.invoiceType}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.invoiceType && Boolean(errors.invoiceType)
                          }
                          helperText={touched.invoiceType && errors.invoiceType}
                        >
                          {invoiceTypes.map((opt) => (
                            <MenuItem key={opt.docTypeId} value={opt.docDescription}>
                              {opt.docDescription}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          name="invoiceDate"
                          label="Invoice Date"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          value={values.invoiceDate}
                          onChange={(e) => {
                            handleChange(e);
                            resetRateDependencies();
                          }}
                          onBlur={handleBlur}
                          error={
                            touched.invoiceDate && Boolean(errors.invoiceDate)
                          }
                          helperText={touched.invoiceDate && errors.invoiceDate}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          name="invoiceNumber"
                          label="Invoice Number"
                          fullWidth
                          value={values.invoiceNumber}
                          onChange={(e) => {
                            handleChange(e);
                            setInvoiceNumberCheck({ checking: false, exists: false, message: "" });
                          }}
                          onBlur={(e) => {
                            handleBlur(e);
                            checkInvoiceNumber(e.target.value);
                          }}
                          error={invoiceNumberCheck.exists}
                          helperText={invoiceNumberCheck.message}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} mt={1}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          select
                          name="scenarioId"
                          label="Scenario ID"
                          fullWidth
                          value={values.scenarioId}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        >
                          {scenarioOptions.map((scenarioId) => (
                            <MenuItem key={scenarioId} value={scenarioId}>
                              {scenarioId}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          name="invoiceRefNo"
                          label="Invoice Ref No"
                          fullWidth
                          value={values.invoiceRefNo}
                          error={
                            touched.invoiceRefNo &&
                            Boolean(errors.invoiceRefNo)
                          }
                          helperText={
                            touched.invoiceRefNo && errors.invoiceRefNo
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          name="reason"
                          label="Reason"
                          fullWidth
                          value={values.reason}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} mt={1}>
                      <Grid item xs={12} sm={6}>
                        <Typography fontWeight={600}>
                          Seller Information
                        </Typography>
                        <TextField
                          name="sellerNTNCNIC"
                          label="NTN/CNIC"
                          fullWidth
                          value={values.sellerNTNCNIC}
                          onChange={handleChange}
                          sx={{ mt: 1 }}
                          error={
                            touched.sellerNTNCNIC &&
                            Boolean(errors.sellerNTNCNIC)
                          }
                          helperText={
                            touched.sellerNTNCNIC && errors.sellerNTNCNIC
                          }
                        />
                        <TextField
                          name="sellerBusinessName"
                          label="Business Name"
                          fullWidth
                          value={values.sellerBusinessName}
                          onChange={handleChange}
                          error={
                            touched.sellerBusinessName &&
                            Boolean(errors.sellerBusinessName)
                          }
                          helperText={
                            touched.sellerBusinessName && errors.sellerBusinessName
                          }
                          sx={{ mt: 1 }}
                        />
                        <TextField
                          select
                          name="sellerProvince"
                          label="Province"
                          fullWidth
                          value={values?.sellerProvince}
                          error={
                            touched.sellerProvince &&
                            Boolean(errors.sellerProvince)
                          }
                          helperText={
                            touched.sellerProvince && errors.sellerProvince
                          }
                          onChange={(e) => {
                            handleChange(e);
                            resetRateDependencies();
                          }}
                          sx={{ mt: 1 }}
                        >
                          {provinces?.map((opt) => (
                            <MenuItem
                              key={opt.stateProvinceCode}
                              value={opt.stateProvinceDesc}
                            >
                              {opt.stateProvinceDesc}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          name="sellerAddress"
                          label="Address"
                          fullWidth
                          value={values.sellerAddress}
                          onChange={handleChange}
                          error={
                            touched.sellerAddress &&
                            Boolean(errors.sellerAddress)
                          }
                          helperText={
                            touched.sellerAddress && errors.sellerAddress
                          }
                          sx={{ mt: 1 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography fontWeight={600}>
                          Buyer Information
                        </Typography>
                        <TextField
                          name="buyerNTNCNIC"
                          label="NTN/CNIC"
                          fullWidth
                          value={values.buyerNTNCNIC}
                          error={
                            touched.buyerNTNCNIC &&
                            Boolean(errors.buyerNTNCNIC)
                          }
                          helperText={
                            touched.buyerNTNCNIC && errors.buyerNTNCNIC
                          }
                          onChange={(e) => {
                            handleChange(e);
                            fetchBuyerSuggestions(e.target.value);
                          }}
                          onFocus={() => {
                            if (buyerSuggestions.length) setShowBuyerSuggestions(true);
                          }}
                          sx={{ mt: 1 }}
                        />
                        {renderBuyerSuggestions(values, setFieldValue)}
                        <TextField
                          name="buyerBusinessName"
                          label="Business Name"
                          fullWidth
                          value={values.buyerBusinessName}
                          error={
                            touched.buyerBusinessName &&
                            Boolean(errors.buyerBusinessName)
                          }
                          helperText={
                            touched.buyerBusinessName && errors.buyerBusinessName
                          }
                          onChange={(e) => {
                            handleChange(e);
                            fetchBuyerSuggestions(e.target.value);
                          }}
                          onFocus={() => {
                            if (buyerSuggestions.length) setShowBuyerSuggestions(true);
                          }}
                          sx={{ mt: 1 }}
                        />
                        {renderBuyerSuggestions(values, setFieldValue)}
                        <TextField
                          name="buyerSalesTaxNo"
                          label="Sales Tax No"
                          fullWidth
                          value={values.buyerSalesTaxNo}
                          onChange={(e) => {
                            handleChange(e);
                            fetchBuyerSuggestions(e.target.value);
                          }}
                          onFocus={() => {
                            if (buyerSuggestions.length) setShowBuyerSuggestions(true);
                          }}
                          onBlur={handleBlur}
                          sx={{ mt: 1 }}
                        />
                        {renderBuyerSuggestions(values, setFieldValue)}
                        <TextField
                          select
                          name="buyerProvince"
                          label="Province"
                          fullWidth
                          value={values.buyerProvince}
                          error={
                            touched.buyerProvince &&
                            Boolean(errors.buyerProvince)
                          }
                          helperText={
                            touched.buyerProvince && errors.buyerProvince
                          }
                          onChange={handleChange}
                          sx={{ mt: 1 }}
                        >
                          {provinces?.map((opt) => (
                            <MenuItem
                              key={opt.stateProvinceCode}
                              value={opt.stateProvinceDesc}
                            >
                              {opt.stateProvinceDesc}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          name="buyerAddress"
                          label="Address"
                          fullWidth
                          error={
                            touched.buyerAddress &&
                            Boolean(errors.buyerAddress)
                          }
                          helperText={
                            touched.buyerAddress && errors.buyerAddress
                          }
                          value={values.buyerAddress}
                          onChange={handleChange}
                          sx={{ mt: 1 }}
                        />
                        <TextField
                          select
                          name="buyerRegistrationType"
                          label="Registration Type"
                          fullWidth
                          value={values.buyerRegistrationType}
                          error={
                            touched.buyerRegistrationType &&
                            Boolean(errors.buyerRegistrationType)
                          }
                          helperText={
                            touched.buyerRegistrationType && errors.buyerRegistrationType
                          }
                          onChange={handleChange}
                          sx={{ mt: 1 }}
                        >
                          {registrationTypes?.map((opt) => (
                            <MenuItem key={opt.statuscode} value={opt.REGISTRATION_TYPE}>
                              {opt.REGISTRATION_TYPE}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} mt={1}>
                      <Grid item xs={12}>
                        <TextField
                          name="termsOfSale"
                          label="Terms of Sale"
                          fullWidth
                          value={values.termsOfSale}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* --- Items Section: 3 fields in one row --- */}
                <Typography variant="h6" fontWeight={600} mb={1}>
                  Items
                </Typography>
                <Card variant="outlined" sx={{ p: 3, mb: 2 }}>
                  {itemFieldsRows.map((row, rowIdx) => (
                    <Grid
                      container
                      spacing={3}
                      alignItems="center"
                      key={rowIdx}
                      sx={{ mb: 1.5 }}
                    >
                      {row.map((f) => (
                        <Grid item xs={4} key={f.key}>
                          {["hsCode","uoM","saleType", "rate", "sroScheduleNo", "sroItemSerialNo"].includes(f.key) ? (
                            <TextField
                              select
                              name={f.key}
                              label={f.label}
                              fullWidth
                              value={itemForm[f.key]}
                              disabled={isItemSelectDisabled(f.key, values)}
                              SelectProps={{
                                onOpen: async () => {
                                  if (isItemSelectDisabled(f.key, values)) return;
                                  try {
                                    if (f.key === "rate" && !rates.length) {
                                      await fetchRatesForItem(values);
                                    }
                                    if (f.key === "sroScheduleNo" && !sroSchedules.length) {
                                      await fetchSroSchedulesForItem(values);
                                    }
                                    if (f.key === "sroItemSerialNo" && !sroItems.length) {
                                      await fetchSroItemsForItem(values);
                                    }
                                  } catch (error) {
                                    console.error("Failed to fetch FBR dependent dropdown:", error);
                                  }
                                }
                              }}
                              onFocus={async () => {
                                if (isItemSelectDisabled(f.key, values)) return;
                                try {
                                  if (f.key === "rate" && !rates.length) {
                                    await fetchRatesForItem(values);
                                  }
                                  if (f.key === "sroScheduleNo" && !sroSchedules.length) {
                                    await fetchSroSchedulesForItem(values);
                                  }
                                  if (f.key === "sroItemSerialNo" && !sroItems.length) {
                                    await fetchSroItemsForItem(values);
                                  }
                                } catch (error) {
                                  console.error("Failed to fetch FBR dependent dropdown:", error);
                                }
                              }}
                              onChange={async (e) => {
                                const value = e.target.value;
                                const nextItemForm = {
                                  ...itemForm,
                                  [f.key]: value,
                                };

                                if (f.key === "saleType") {
                                  const selectedSaleType = saleTypes.find((opt) => getSaleTypeDesc(opt) === value);
                                  nextItemForm.saleTypeId = getSaleTypeId(selectedSaleType);
                                  nextItemForm.rate = "";
                                  nextItemForm.rateId = "";
                                  nextItemForm.sroScheduleNo = "";
                                  nextItemForm.sroScheduleId = "";
                                  nextItemForm.sroItemSerialNo = "";
                                  setRates([]);
                                  setSroSchedules([]);
                                  setSroItems([]);

                                  if (nextItemForm.saleTypeId) {
                                    try {
                                      await fetchRatesForItem(values, nextItemForm);
                                    } catch (error) {
                                      console.error("Failed to fetch FBR rates:", error);
                                    }
                                  }
                                }

                                if (f.key === "rate") {
                                  const selectedRate = rates.find((opt) => getRateDesc(opt) === value);
                                  nextItemForm.rateId = getRateId(selectedRate);
                                  nextItemForm.sroScheduleNo = "";
                                  nextItemForm.sroScheduleId = "";
                                  nextItemForm.sroItemSerialNo = "";
                                  setSroSchedules([]);
                                  setSroItems([]);

                                  if (nextItemForm.rateId) {
                                    try {
                                      await fetchSroSchedulesForItem(values, nextItemForm);
                                    } catch (error) {
                                      console.error("Failed to fetch FBR SRO schedules:", error);
                                    }
                                  }
                                }

                                if (f.key === "sroScheduleNo") {
                                  const selectedSro = sroSchedules.find((opt) => getSroDesc(opt) === value);
                                  nextItemForm.sroScheduleId = getSroId(selectedSro);
                                  nextItemForm.sroItemSerialNo = "";
                                  setSroItems([]);

                                  if (nextItemForm.sroScheduleId) {
                                    try {
                                      await fetchSroItemsForItem(values, nextItemForm);
                                    } catch (error) {
                                      console.error("Failed to fetch FBR SRO items:", error);
                                    }
                                  }
                                }

                                if (f.key === "hsCode") {
                                  const selectedHsCode = availableHsCodes.find((opt) => getHsCode(opt) === value);
                                  const configuredSaleType = getHsSaleType(selectedHsCode);
                                  const selectedSaleType = saleTypes.find(
                                    (opt) => String(getSaleTypeDesc(opt)).toLowerCase() === String(configuredSaleType).toLowerCase()
                                  );
                                  nextItemForm.productDescription = getHsDesc(selectedHsCode) || nextItemForm.productDescription;
                                  nextItemForm.uoM = getHsUom(selectedHsCode) || nextItemForm.uoM;
                                  nextItemForm.saleType = configuredSaleType || nextItemForm.saleType;
                                  nextItemForm.saleTypeId = getHsSaleTypeId(selectedHsCode) || (selectedSaleType ? getSaleTypeId(selectedSaleType) : nextItemForm.saleTypeId);
                                  nextItemForm.rate = getHsRate(selectedHsCode) || nextItemForm.rate;
                                  nextItemForm.rateId = getHsRateId(selectedHsCode) || nextItemForm.rateId;
                                  nextItemForm.sroScheduleNo = getHsSroScheduleNo(selectedHsCode) || nextItemForm.sroScheduleNo;
                                  nextItemForm.sroScheduleId = getHsSroScheduleId(selectedHsCode) || nextItemForm.sroScheduleId;
                                  nextItemForm.sroItemSerialNo = getHsSroItemNo(selectedHsCode) || nextItemForm.sroItemSerialNo;
                                }

                                setItemForm(nextItemForm);
                              }}
                              error={Boolean(itemError[f.key])}
                              helperText={itemError[f.key] || getItemSelectHelperText(f.key, values)}
                            >
                              {f.key === "hsCode" ?
                                availableHsCodes.map((opt) => (
                                  <MenuItem key={getHsCode(opt)} value={getHsCode(opt)}>
                                    {getHsCode(opt)}{getHsDesc(opt) ? ` - ${getHsDesc(opt)}` : ""}
                                  </MenuItem>
                                ))
                                : f.key === "saleType" ?
                                [
                                  itemForm.saleType && !saleTypes.some((opt) => getSaleTypeDesc(opt) === itemForm.saleType) ? (
                                    <MenuItem key={`configured-sale-type-${itemForm.saleType}`} value={itemForm.saleType}>
                                      {itemForm.saleType}
                                    </MenuItem>
                                  ) : null,
                                  ...saleTypes.map((opt) => (
                                    <MenuItem
                                      key={getSaleTypeId(opt)}
                                      value={getSaleTypeDesc(opt)}
                                    >
                                      {getSaleTypeDesc(opt)}
                                    </MenuItem>
                                  ))
                                ]
                                : f.key === "rate" ?
                                [
                                  itemForm.rate && !rates.some((opt) => getRateDesc(opt) === itemForm.rate) ? (
                                    <MenuItem key={`configured-rate-${itemForm.rate}`} value={itemForm.rate}>
                                      {itemForm.rate}
                                    </MenuItem>
                                  ) : null,
                                  ...rates.map((opt) => (
                                    <MenuItem key={getRateId(opt)} value={getRateDesc(opt)}>
                                      {getRateDesc(opt)}
                                    </MenuItem>
                                  ))
                                ]
                                : f.key === "sroScheduleNo" ?
                                [
                                  <MenuItem key="empty-sro" value="">No SRO Schedule</MenuItem>,
                                  itemForm.sroScheduleNo && !sroSchedules.some((opt) => getSroDesc(opt) === itemForm.sroScheduleNo) ? (
                                    <MenuItem key={`configured-sro-${itemForm.sroScheduleNo}`} value={itemForm.sroScheduleNo}>
                                      {itemForm.sroScheduleNo}
                                    </MenuItem>
                                  ) : null,
                                  ...sroSchedules.map((opt) => (
                                    <MenuItem key={getSroId(opt)} value={getSroDesc(opt)}>
                                      {getSroDesc(opt)}
                                    </MenuItem>
                                  ))
                                ]
                                : f.key === "sroItemSerialNo" ?
                                [
                                  <MenuItem key="empty-sro-item" value="">No SRO Item</MenuItem>,
                                  itemForm.sroItemSerialNo && !sroItems.some((opt) => getSroItemDesc(opt) === itemForm.sroItemSerialNo) ? (
                                    <MenuItem key={`configured-sro-item-${itemForm.sroItemSerialNo}`} value={itemForm.sroItemSerialNo}>
                                      {itemForm.sroItemSerialNo}
                                    </MenuItem>
                                  ) : null,
                                  ...sroItems.map((opt) => (
                                    <MenuItem key={getSroItemId(opt)} value={getSroItemDesc(opt)}>
                                      {getSroItemDesc(opt)}
                                    </MenuItem>
                                  ))
                                ]
                                : [
                                  itemForm.uoM && !uoms.some((opt) => opt.description === itemForm.uoM) ? (
                                    <MenuItem key={`configured-uom-${itemForm.uoM}`} value={itemForm.uoM}>
                                      {itemForm.uoM}
                                    </MenuItem>
                                  ) : null,
                                  ...uoms.map((opt) => (
                                      <MenuItem
                                        key={opt.uoM_ID}
                                        value={opt.description}
                                      >
                                        {opt.description}
                                      </MenuItem>
                                    ))
                                ]}
                            </TextField>
                          ) : (
                            <TextField
                              name={f.key}
                              label={f.label}
                              fullWidth
                              type={
                                [
                                  "quantity",
                                  "totalValues",
                                  "valueSalesExcludingST",
                                  "fixedNotifiedValueOrRetailPrice",
                                  "salesTaxApplicable",
                                  "salesTaxWithheldAtSource",
                                  "fedPayable",
                                  "discount",
                                  "furtherTax",
                                ].includes(f.key)
                                  ? "number"
                                  : "text"
                              }
                              value={itemForm[f.key]}
                              onChange={(e) =>
                                setItemForm({
                                  ...itemForm,
                                  [f.key]: e.target.value,
                                })
                              }
                              error={Boolean(itemError[f.key])}
                              helperText={itemError[f.key] || ""}
                            />
                          )}
                        </Grid>
                      ))}
                      {/* Add/Save button on the last row */}
                      {rowIdx === itemFieldsRows.length - 1 && (
                        <Grid item xs={4}>
                          <Button
                            type="button"
                            variant={
                              itemEditIndex !== null ? "contained" : "outlined"
                            }
                            startIcon={
                              itemEditIndex !== null ? (
                                <EditIcon />
                              ) : (
                                <AddIcon />
                              )
                            }
                            fullWidth
                            sx={{ height: "100%" }}
                            onClick={async () => {
                              const errors = await validateItem(itemForm);
                              setItemError(errors);
                              if (Object.keys(errors).length > 0) return;
                              if (itemEditIndex !== null) {
                                const newItems = [...values.items];
                                newItems[itemEditIndex] = itemForm;
                                setFieldValue("items", newItems);
                                setItemEditIndex(null);
                              } else {
                                setFieldValue("items", [
                                  ...values.items,
                                  itemForm,
                                ]);
                              }
                              setItemForm({
                                ...initialItem,
                                uoM: uoms.length ? uoms[0].description : "",
                                saleType: saleTypes.length ? getSaleTypeDesc(saleTypes[0]) : "",
                                saleTypeId: saleTypes.length ? getSaleTypeId(saleTypes[0]) : "",
                              });
                              setItemError({});
                            }}
                          >
                            {itemEditIndex !== null ? "Save Item" : "Add Item"}
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  ))}
                </Card>
                {errors.items && (
                  <Typography color="error" fontSize={14} mb={1}>
                    {typeof errors.items === "string" ? errors.items : ""}
                  </Typography>
                )}

                {/* --- Items Table/List --- */}
                {values.items.length > 0 && (
                  <Paper
                    variant="outlined"
                    sx={{ mb: 2, maxHeight: 240, overflow: "auto" }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {itemFields.map((f) => (
                            <TableCell key={f.key}>{f.label}</TableCell>
                          ))}
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {values.items.map((item, idx) => (
                          <TableRow key={idx}>
                            {itemFields.map((f) => (
                              <TableCell key={f.key}>{item[f.key]}</TableCell>
                            ))}
                            <TableCell>
                              <Tooltip title="Edit">
                                <IconButton
                                  color="primary"
                                  onClick={() => {
                                    setItemForm(item);
                                    setItemEditIndex(idx);
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  color="error"
                                  onClick={() => {
                                    const newItems = values.items.filter(
                                      (_, i) => i !== idx
                                    );
                                    setFieldValue("items", newItems);
                                    if (itemEditIndex === idx) {
                                      setItemForm({
                                        ...initialItem,
                                        uoM: uoms.length ? uoms[0].description : "",
                                        saleType: saleTypes.length ? getSaleTypeDesc(saleTypes[0]) : "",
                                        saleTypeId: saleTypes.length ? getSaleTypeId(saleTypes[0]) : "",
                                      });
                                      setItemEditIndex(null);
                                    }
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={onClose}
                  color="secondary"
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting || invoiceNumberCheck.checking || invoiceNumberCheck.exists}
                >
                  Save Invoice
                </Button>
              </DialogActions>
            </form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default InvoiceForm;
